import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getApis } from '../../library/Apis';
import ApiService from '../../services/ApiService';

export interface SimulateAsyncOperationOptions {
  hasProgress?: boolean;
  onProgressUpdate?: (progress: number | null) => void;
}

export const toRequest = <Type = any>(
  requestMethod:
    | typeof ApiService.options
    | typeof ApiService.get
    | typeof ApiService.post
    | typeof ApiService.put
    | typeof ApiService.patch
    | typeof ApiService.delete,
  requestMethodOnlineParams:
    | [string]
    | [string, AxiosRequestConfig | any]
    | [string, any, AxiosRequestConfig],
  offlineJSONName?: string,
  mock?: boolean
): Promise<AxiosResponse<Type>> => {
  if (getApis()?.isOffline || mock) {
    const url = `assets/mock/${offlineJSONName}.json`;
    return ApiService.get(url);
  } else {
    return requestMethod.apply({}, requestMethodOnlineParams as any) as Promise<
      AxiosResponse<Type>
    >;
  }
};

export const getErrorMessage = (errorRequest: any) => {
  if (typeof errorRequest === 'object') {
    if (
      typeof errorRequest.details === 'object' &&
      typeof errorRequest.details.length === 'number' &&
      errorRequest.details.length > 0
    ) {
      return errorRequest.details[0].message;
    } else {
      return errorRequest.message;
    }
  }
  return null;
};

export const getMergedParamsFromObject = (obj: any) => {
  return Object.entries(obj)
    .filter(
      (it) =>
        it[1] !== undefined &&
        it[1] !== null &&
        it[1] !== '' &&
        (!Array.isArray(it[1]) || it[1].length)
    )
    .map((it) => {
      return `${it[0]}=${it[1]}`;
    })
    .join('&');
};

export const getMergedParamsFromObjectFiltered = (obj: any) => {
  const arr = [];
  for (let i in obj) {
    const value = obj[i];
    if (Array.isArray(value)) {
      if (value.length) {
        arr.push({ id: i, value: value.join(',') });
      }
    } else if (value !== undefined && value !== null && value !== '') {
      arr.push({ id: i, value });
    }
  }
  return arr.map((i) => `${i.id}=${i.value}`).join('&');
};

export const ArrayBufferDownloadHeaders = {
  responseType: 'arraybuffer',
  headers: {
    Accept: '*',
    'Access-Control-Allow-Headers':
      'Content-Type, Content-Disposition, Authorization, X-Requested-With',
  },
} as AxiosRequestConfig;

export const BlobDownloadHeaders = {
  responseType: 'blob',
  headers: {
    Accept: '*',
    'Access-Control-Allow-Headers':
      'Content-Type, Content-Disposition, Authorization, X-Requested-With',
  },
} as AxiosRequestConfig;

function buildToastKey(errorKey: string): string {
  return `Toasts.Errors.Keys.${errorKey}`;
}

export const toastKey = (err: AxiosError<Error>): string => {
  const errorKey = (err as AxiosError<Error>).response?.data.message;
  return buildToastKey(errorKey!) as any;
};

export const simulateAsyncOperation = ({
  hasProgress = false,
  onProgressUpdate = () => {},
}: SimulateAsyncOperationOptions): Promise<void> => {
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const randomDelay = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  return new Promise<void>(async (resolve, reject) => {
    try {
      await delay(randomDelay(500, 1500));

      if (hasProgress) {
        let progress = 0;

        while (progress < 100) {
          progress += 1;
          onProgressUpdate(progress);

          await delay(randomDelay(20, 90));
        }

        const isSuccess = Math.random() > 0.5;

        if (isSuccess) {
          await delay(1000).finally(async () => onProgressUpdate(null));
          resolve();
        } else {
          onProgressUpdate(null);
          console.error('Progress Completion Error');
          reject();
        }
      } else {
        await delay(2000);
        const isSuccess = Math.random() > 0.5;

        if (isSuccess) {
          resolve();
        } else {
          console.error('Basic Operation Error');
          reject();
        }
      }
    } catch (error) {
      console.error('Async Operation Execution Error', error);
      reject(error);
    }
  });
};

export const getContentDispositionFileName = (response: AxiosResponse<any>) => {
  const contentDisposition = response?.headers['content-disposition'];
  return contentDisposition?.slice(contentDisposition.indexOf('=') + 1) ?? '';
};
